import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ArrowRight } from "lucide-react";
import { executeRecaptcha } from "../lib/recaptcha";
import RecaptchaNotice from "./RecaptchaNotice";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DetailedForm() {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    property_address: "",
    condition: "",
    timeline: "",
    reason: "",
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const u = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.property_address) {
      toast.error("Please fill in the required fields.");
      return;
    }
    if (!consent) {
      toast.error("Please agree to be contacted before submitting.");
      return;
    }
    setLoading(true);
    try {
      const { token, action } = await executeRecaptcha("lead_detailed");
      await axios.post(`${API}/leads`, {
        ...form,
        source: "detailed",
        consent: true,
        recaptcha_token: token,
        recaptcha_action: action,
      });
      trackEvent("lead_submit", { form: "detailed" });
      trackEvent("generate_lead", { form: "detailed", currency: "USD", value: 1 });
      setDone(true);
      toast.success("We got it. Expect a call within 24 hours.");
      setForm({
        full_name: "",
        phone: "",
        email: "",
        property_address: "",
        condition: "",
        timeline: "",
        reason: "",
      });
      setConsent(false);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please call (910) 212-5006.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="offer"
      data-testid="detailed-form-section"
      className="border-b border-[var(--c4p-line)] bg-[var(--c4p-ivory)] py-20 lg:py-28"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10">
        <div className="lg:col-span-5">
          <span className="eyebrow">Get your offer</span>
          <h2 className="font-display mt-4 text-[40px] leading-[1.02] sm:text-[52px] lg:text-[60px]">
            Tell us a bit more.{" "}
            <span className="font-display-italic text-[var(--c4p-orange)]">We'll do the rest.</span>
          </h2>
          <p className="mt-6 max-w-md font-sans-ui text-[15px] leading-relaxed text-[var(--c4p-muted)]">
            The more you share, the faster and more accurate your cash offer
            will be. Every submission is reviewed by a real person — never a bot.
          </p>

          <div className="mt-8 overflow-hidden rounded-2xl border border-[var(--c4p-line)]">
            <img
              src="https://images.pexels.com/photos/31602311/pexels-photo-31602311.jpeg"
              alt="A red brick home that sold for cash"
              className="h-64 w-full object-cover"
              loading="lazy"
            />
          </div>

          <ul className="mt-6 space-y-3">
            {[
              "No obligation — ever.",
              "We pay all standard closing costs.",
              "Close in as little as 7 days.",
            ].map((t) => (
              <li
                key={t}
                className="flex items-center gap-3 font-sans-ui text-[14px] text-[var(--c4p-ink)]/80"
              >
                <ShieldCheck size={16} className="text-[var(--c4p-forest)]" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-[var(--c4p-line)] bg-white p-8 shadow-[0_30px_60px_-40px_rgba(12,59,46,.2)]">
            {done ? (
              <div
                data-testid="detailed-form-success"
                className="grid place-items-center py-16 text-center"
              >
                <div className="font-display text-[30px] text-[var(--c4p-forest)]">
                  Thanks — your request is in.
                </div>
                <p className="mt-3 max-w-md font-sans-ui text-sm text-[var(--c4p-muted)]">
                  We've emailed Triston and he'll personally call you within 24
                  hours.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="detailed-form-reset-btn"
                  className="mt-6 font-sans-ui text-[12px] font-semibold uppercase tracking-widest text-[var(--c4p-orange)] underline"
                >
                  Submit another property
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Labeled label="Full Name">
                  <input
                    data-testid="d-name"
                    className="input-ui"
                    value={form.full_name}
                    onChange={(e) => u("full_name", e.target.value)}
                    placeholder="Jane Doe"
                  />
                </Labeled>
                <Labeled label="Phone">
                  <input
                    data-testid="d-phone"
                    type="tel"
                    className="input-ui"
                    value={form.phone}
                    onChange={(e) => u("phone", e.target.value)}
                    placeholder="(910) 555-0123"
                  />
                </Labeled>
                <Labeled label="Email">
                  <input
                    data-testid="d-email"
                    type="email"
                    className="input-ui"
                    value={form.email}
                    onChange={(e) => u("email", e.target.value)}
                    placeholder="jane@example.com"
                  />
                </Labeled>
                <Labeled label="Property Address">
                  <input
                    data-testid="d-address"
                    className="input-ui"
                    value={form.property_address}
                    onChange={(e) => u("property_address", e.target.value)}
                    placeholder="123 Main St, Greenville, NC"
                  />
                </Labeled>
                <Labeled label="Condition">
                  <select
                    data-testid="d-condition"
                    className="input-ui"
                    value={form.condition}
                    onChange={(e) => u("condition", e.target.value)}
                  >
                    <option value="">Select condition</option>
                    <option>Move-in ready</option>
                    <option>Needs minor repairs</option>
                    <option>Needs major repairs</option>
                    <option>Tear-down</option>
                  </select>
                </Labeled>
                <Labeled label="Timeline">
                  <select
                    data-testid="d-timeline"
                    className="input-ui"
                    value={form.timeline}
                    onChange={(e) => u("timeline", e.target.value)}
                  >
                    <option value="">How soon?</option>
                    <option>ASAP (within 7 days)</option>
                    <option>30 days</option>
                    <option>60–90 days</option>
                    <option>Just exploring</option>
                  </select>
                </Labeled>
                <Labeled label="Reason for Selling (optional)" full>
                  <textarea
                    data-testid="d-reason"
                    className="input-ui"
                    rows={3}
                    value={form.reason}
                    onChange={(e) => u("reason", e.target.value)}
                    placeholder="Relocation, inheritance, foreclosure concerns, tenant issues…"
                  />
                </Labeled>

                <div className="sm:col-span-2">
                  <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-lg border border-[var(--c4p-line)] bg-[var(--c4p-cream)]/50 p-3.5">
                    <input
                      type="checkbox"
                      data-testid="detailed-consent-checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--c4p-orange)]"
                    />
                    <span className="font-sans-ui text-[12.5px] leading-snug text-neutral-700">
                      I agree to be contacted by Cash 4 Properties at the
                      phone and email above, including by call, SMS, or
                      automated messages. Consent is not required to receive
                      an offer. Message/data rates may apply. See our{" "}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Terms
                      </a>
                      .
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="detailed-submit-btn"
                    className="btn-primary w-full disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={16} /> Sending…
                      </>
                    ) : (
                      <>
                        Request My Cash Offer <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                  <RecaptchaNotice className="mt-3 text-center" />
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Labeled({ label, children, full }) {
  return (
    <label className={full ? "sm:col-span-2" : ""}>
      <span className="mb-1.5 block font-sans-ui text-[12.5px] font-semibold text-[var(--c4p-ink)]">
        {label}
      </span>
      {children}
    </label>
  );
}
